/**
 * MarketingDashboardPage — kerncijfers van het marketing-platform.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMarketingStore } from '../../stores/marketing-store';
import { MarketingNav, MarketingPageHeader, SectionCard, StatTile, StatusBadge, formatDateTime } from './shared';
import { MarketingSendDrawer } from './drawers';

/** "kleda-alle: +4 / -1"-regels uit het rauwe sync-resultaat. */
function syncSummaryLines(summary: unknown): string[] {
  const s = summary as { segments?: Array<{ key?: string; added?: number; removed?: number; members?: number }> };
  if (!Array.isArray(s?.segments)) return [];
  return s.segments.map(
    (seg) =>
      `${seg.key ?? '?'}: ${(seg.members ?? 0).toLocaleString('nl-NL')} leden (+${seg.added ?? 0} / -${seg.removed ?? 0})`
  );
}

export function MarketingDashboardPage() {
  const { dashboard, isLoading, error, fetchDashboard } = useMarketingStore();
  const [openSendId, setOpenSendId] = useState<string | null>(null);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const sends = dashboard?.sends30d ?? {};
  const events = dashboard?.events30d ?? {};
  const totalSent = (sends.sent ?? 0) + (sends.delivered ?? 0) + (sends.bounced ?? 0) + (sends.complained ?? 0);
  const deliveredPct = totalSent > 0 ? Math.round(((sends.delivered ?? 0) / totalSent) * 100) : 0;

  return (
    <>
      <div className="flex-1 overflow-y-auto">
        <MarketingPageHeader title="E-mailmarketing" subtitle="Campagnes, journeys, segmenten en contacten — Supabase als waarheid, Resend als verzendkanaal" />
        <MarketingNav />

        <div className="p-6 space-y-6">
          {error && <div className="bg-red-50 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>}
          {isLoading && !dashboard && <p className="text-sm text-gray-400">Laden…</p>}

          {dashboard && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <StatTile label="Contacten" value={dashboard.contacts.total.toLocaleString('nl-NL')} sub={`${dashboard.contacts.subscribed.toLocaleString('nl-NL')} ingeschreven`} />
                <StatTile label="Verzonden (30d)" value={totalSent.toLocaleString('nl-NL')} sub={`${deliveredPct}% bezorgd`} />
                <StatTile label="Geopend (30d)" value={(events.opened ?? 0).toLocaleString('nl-NL')} />
                <StatTile label="Geklikt (30d)" value={(events.clicked ?? 0).toLocaleString('nl-NL')} />
                <StatTile label="Bounces (30d)" value={(sends.bounced ?? 0).toLocaleString('nl-NL')} sub={`${events.complained ?? 0} klachten`} />
                <StatTile label="In journey" value={dashboard.contactsInJourney.toLocaleString('nl-NL')} sub={`${dashboard.activeJourneys.length} actieve journeys`} />
              </div>

              <div className="grid lg:grid-cols-2 gap-6">
                <SectionCard title="Actieve journeys">
                  {dashboard.activeJourneys.length === 0 ? (
                    <p className="text-sm text-gray-400">
                      Geen actieve journeys.{' '}
                      <Link to="/marketing/journeys" className="text-indigo-600 hover:underline">
                        Journey aanmaken
                      </Link>
                    </p>
                  ) : (
                    <ul className="divide-y divide-gray-100">
                      {dashboard.activeJourneys.map((j) => (
                        <li key={j.id} className="py-2 flex items-center justify-between">
                          <Link to={`/marketing/journeys/${j.id}`} className="text-sm text-indigo-600 hover:underline">
                            {j.name}
                          </Link>
                          <StatusBadge status={j.status} />
                        </li>
                      ))}
                    </ul>
                  )}
                </SectionCard>

                <SectionCard title="Laatste contactsync">
                  {dashboard.lastSync?.at ? (
                    <div className="text-sm text-gray-600 space-y-1">
                      <p>
                        <span className="text-gray-400">Moment:</span> {formatDateTime(dashboard.lastSync.at)}
                      </p>
                      {dashboard.lastSync.summary && (
                        <ul className="text-xs text-gray-400 space-y-0.5">
                          {syncSummaryLines(dashboard.lastSync.summary).map((line) => (
                            <li key={line}>{line}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400">
                      Nog geen sync gedraaid — de nachtelijke cron (02:30) vult het contactenbestand.
                    </p>
                  )}
                </SectionCard>
              </div>

              <SectionCard title="Recente sends">
                {dashboard.recentSends.length === 0 ? (
                  <p className="text-sm text-gray-400">Nog geen sends.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-gray-400">
                        <th className="pb-2 font-medium">Ontvanger</th>
                        <th className="pb-2 font-medium">Onderwerp</th>
                        <th className="pb-2 font-medium">Bron</th>
                        <th className="pb-2 font-medium">Status</th>
                        <th className="pb-2 font-medium">Moment</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {dashboard.recentSends.map((s) => {
                        const send = s as {
                          id: string;
                          to_email: string;
                          subject: string | null;
                          status: string;
                          sent_at: string | null;
                          queued_at: string;
                          marketing_campaigns?: { name: string } | null;
                          marketing_journeys?: { name: string } | null;
                        };
                        return (
                          <tr
                            key={send.id}
                            onClick={() => setOpenSendId(send.id)}
                            className="cursor-pointer hover:bg-slate-50"
                            title="Bekijk de mail zoals verstuurd"
                          >
                            <td className="py-2 text-gray-900">{send.to_email}</td>
                            <td className="py-2 text-gray-600 max-w-xs truncate">{send.subject ?? '—'}</td>
                            <td className="py-2 text-gray-500">
                              {send.marketing_campaigns?.name ?? send.marketing_journeys?.name ?? '—'}
                            </td>
                            <td className="py-2">
                              <StatusBadge status={send.status} />
                            </td>
                            <td className="py-2 text-gray-500">{formatDateTime(send.sent_at ?? send.queued_at)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </SectionCard>
            </>
          )}
        </div>
      </div>
      {openSendId && <MarketingSendDrawer sendId={openSendId} onClose={() => setOpenSendId(null)} />}
    </>
  );
}
